import React, { useState } from 'react';
import { fetchWithAuth } from '../services/api';

const money = (n) =>
  n === null || n === undefined || n === '' || Number.isNaN(Number(n))
    ? '—'
    : `$${Number(n).toFixed(2)}`;

const confidenceClass = (level) => {
  if (level === 'high') return 'bg-green-100 text-green-800';
  if (level === 'medium') return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
};

const MortgageExpensesPage = () => {
  const [file, setFile] = useState(null);
  const [extractLoading, setExtractLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);
  const [review, setReview] = useState(null);

  const resetReview = () => {
    setReview(null);
    setSubmitResult(null);
    setError(null);
  };

  const handleExtract = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Please choose a mortgage statement PDF.');
      return;
    }

    setExtractLoading(true);
    setError(null);
    setSubmitResult(null);
    setReview(null);

    try {
      const formData = new FormData();
      formData.append('pdf', file);
      const response = await fetchWithAuth('/api/expenses/mortgage/extract', {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Extraction failed');
      }
      setReview(result);
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setExtractLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!file || !review?.extraction) {
      setError('Extract a statement before submitting.');
      return;
    }

    setSubmitLoading(true);
    setError(null);
    setSubmitResult(null);

    try {
      const formData = new FormData();
      if (review.pdf_token) {
        formData.append('pdf_token', review.pdf_token);
      }
      if (file) {
        formData.append('pdf', file);
      }
      formData.append('extraction', JSON.stringify(review.extraction));
      const response = await fetchWithAuth('/api/expenses/mortgage/submit', {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();
      if (result.status === 'failed') {
        setSubmitResult(result);
        throw new Error(
          result.stage ? `Failed at ${result.stage}: ${result.error}` : result.error
        );
      }
      if (!response.ok) {
        throw new Error(result.error || 'Submit failed');
      }
      setSubmitResult(result);
    } catch (err) {
      setError(err.message || 'Submit failed');
    } finally {
      setSubmitLoading(false);
    }
  };

  const ext = review?.extraction;
  const breakdown = ext?.payment_breakdown;
  const bill = review?.payload_preview?.data?.[0];
  const lineItems = bill?.LineItems || [];

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-2">Mortgage statements</h3>
        <p className="text-sm text-gray-600 mb-4">
          Upload a servicer PDF, review extracted P+I+E and notes, then post a paid bill to
          AppFolio with the statement attached. Extraction uses Claude; posting is a separate
          step so you can verify before sending.
        </p>

        <form onSubmit={handleExtract} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statement PDF
            </label>
            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={(ev) => {
                setFile(ev.target.files?.[0] || null);
                resetReview();
              }}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {file && (
              <p className="text-xs text-gray-500 mt-1">{file.name}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={extractLoading || !file}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              {extractLoading ? 'Extracting…' : '1. Extract & preview'}
            </button>
            {review && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitLoading || !file}
                className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 disabled:bg-gray-400"
              >
                {submitLoading ? 'Posting…' : '2. Post bill to AppFolio'}
              </button>
            )}
          </div>
        </form>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm">
            {error}
          </div>
        )}

        {submitResult && (
          <div
            className={`mt-4 p-3 rounded text-sm border ${
              submitResult.status === 'posted'
                ? 'bg-green-50 border-green-200 text-green-800'
                : submitResult.status === 'skipped'
                  ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                  : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {submitResult.status === 'posted' && (
              <>
                <p className="font-medium">Bill posted</p>
                <p>Reference: {submitResult.reference}</p>
                <p>Bill ID: {submitResult.bill_id}</p>
                <p>
                  PDF attached:{' '}
                  {submitResult.attachment_attached ? 'Yes' : 'No — attach manually in AppFolio'}
                </p>
                {submitResult.attachment_error && (
                  <p className="text-xs mt-1 opacity-90">{submitResult.attachment_error}</p>
                )}
              </>
            )}
            {submitResult.status === 'skipped' && (
              <>
                <p className="font-medium">Skipped (already posted)</p>
                <p>{submitResult.reason}</p>
                <p>Reference: {submitResult.reference}</p>
                {submitResult.bill_id && <p>Bill ID: {submitResult.bill_id}</p>}
                {submitResult.attachment_attached != null && (
                  <p>
                    PDF attached:{' '}
                    {submitResult.attachment_attached
                      ? 'Yes'
                      : 'No — attach manually in AppFolio'}
                  </p>
                )}
                {submitResult.attachment_error && (
                  <p className="text-xs mt-1 opacity-90">{submitResult.attachment_error}</p>
                )}
              </>
            )}
            {submitResult.status === 'failed' && (
              <>
                <p className="font-medium">Failed at {submitResult.stage}</p>
                <p>{submitResult.error}</p>
              </>
            )}
          </div>
        )}
      </div>

      {review && ext && (
        <>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h3 className="text-lg font-semibold">Extraction summary</h3>
              <span
                className={`text-xs font-medium px-2 py-1 rounded ${confidenceClass(
                  ext.extraction_confidence
                )}`}
              >
                Confidence: {ext.extraction_confidence}
              </span>
            </div>

            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div>
                <dt className="text-gray-500">Servicer</dt>
                <dd className="font-medium">{ext.servicer}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Loan number</dt>
                <dd className="font-medium">{ext.loan_number}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Borrower</dt>
                <dd>{ext.borrower_entity || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Billing period</dt>
                <dd>{ext.billing_period || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Statement date</dt>
                <dd>{ext.statement_date || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Due / payment date</dt>
                <dd>{ext.due_date}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Bookable total (P+I+E)</dt>
                <dd className="font-semibold text-lg">{money(ext.bookable_total)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Amount due on statement (coupon)</dt>
                <dd>{money(ext.amount_due_statement)}</dd>
              </div>
              {ext.unapplied_fund_balance && (
                <div>
                  <dt className="text-gray-500">Unapplied fund balance</dt>
                  <dd>{money(ext.unapplied_fund_balance)}</dd>
                </div>
              )}
              <div className="md:col-span-2">
                <dt className="text-gray-500">Collateral</dt>
                <dd>{ext.collateral_address || '—'}</dd>
              </div>
            </dl>

            {review.loan_mapping && (
              <div className="mt-4 p-3 bg-gray-50 rounded text-sm">
                <p className="font-medium text-gray-700 mb-1">AppFolio mapping (lenders.yaml)</p>
                <p>Corporate property: {review.loan_mapping.corporate_property}</p>
                <p>Vendor: {review.loan_mapping.vendor}</p>
                <p>Bill reference: {review.reference}</p>
              </div>
            )}
          </div>

          {breakdown && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-3">Payment breakdown</h3>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Component</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-700">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="px-4 py-2">Principal</td>
                    <td className="px-4 py-2 text-right">{money(breakdown.principal)}</td>
                  </tr>
                  <tr className="border-t">
                    <td className="px-4 py-2">Interest</td>
                    <td className="px-4 py-2 text-right">{money(breakdown.interest)}</td>
                  </tr>
                  <tr className="border-t">
                    <td className="px-4 py-2">Escrow</td>
                    <td className="px-4 py-2 text-right">{money(breakdown.escrow)}</td>
                  </tr>
                  <tr className="border-t font-medium">
                    <td className="px-4 py-2">Total</td>
                    <td className="px-4 py-2 text-right">{money(ext.bookable_total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {ext.lender_quirk_notes && (
            <div className="bg-amber-50 border border-amber-200 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-amber-900 mb-2">
                Extraction notes
              </h3>
              <p className="text-sm text-amber-950 whitespace-pre-wrap">
                {ext.lender_quirk_notes}
              </p>
              <p className="text-xs text-amber-700 mt-2">
                Not sent to AppFolio Remarks (field is too short). Logged server-side on post.
              </p>
            </div>
          )}

          {lineItems.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-3">AppFolio bill preview</h3>
              {review.remarks_preview && (
                <p className="text-sm text-gray-600 mb-3">
                  <span className="font-medium">Remarks:</span> {review.remarks_preview}
                </p>
              )}
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Line</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-700">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-4 py-2">{li.Description}</td>
                      <td className="px-4 py-2 text-right">{money(li.Amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-sm text-gray-500 mt-3">
                Total: {money(bill?.TotalAmount)} · Payment ref: {bill?.PaymentReference}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MortgageExpensesPage;
